import type { CommissionStatement } from '../property/buyer-commission';
import type { DocumentAddress, DocumentValue } from './deal-documents.schemas';

export type DocumentTranslator = (key: string, params?: Record<string, unknown>) => string;

export const FILL_IN_LINE = '_'.repeat(24);

const WHOLE_MONEY = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const FRACTIONAL_MONEY = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const PERCENT = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const NAMES = new Intl.ListFormat('de', { type: 'conjunction' });

export function formatDocumentValue(value: DocumentValue, t: DocumentTranslator): string {
  switch (value.kind) {
    case 'DATE': {
      const [year, month, day] = value.date.split('-');

      return `${day}.${month}.${year}`;
    }

    case 'MONEY':
      return (Number.isInteger(value.amount) ? WHOLE_MONEY : FRACTIONAL_MONEY).format(value.amount);
    case 'PERCENT':
      return `${PERCENT.format(value.value)}\u00A0%`;
    case 'COMMISSION_STATEMENT':
      return commissionStatementText(value.statement, t);
    case 'MONTHS':
      return t('documents.deal-document.months', { months: value.months });
    case 'TEXT':
      return value.text;
    case 'ADDRESS':
      return addressLines(value.address).join(', ');
    case 'NAME_LIST':
      return NAMES.format(value.names);
    case 'LINES':
      return value.lines.join('\n');
    case 'BROKER_REPRESENTATION':
      return t('documents.deal-document.broker-representation', {
        count: value.names.length,
        names: NAMES.format(value.names),
      });
    case 'ADDRESS_BLOCKS':
      return value.blocks
        .map((block) =>
          block.address === null
            ? `${block.name}\n${FILL_IN_LINE}`
            : [block.name, ...addressLines(block.address)].join('\n'),
        )
        .join('\n\n');
    case 'MANDATE_TYPE':
      return t(`pages.workspace.deals.mandate-type.${value.token}`);
    case 'LEGAL_FORM':
      return t(`pages.settings.company.legal-forms.${value.token}`);
    case 'PROPERTY_TYPE':
      return value.subtype === null
        ? t(`common.immobilientyp.category.${value.category}`)
        : t(`common.immobilientyp.subtype.${value.subtype}`);
    default:
      return assertNever(value);
  }
}

function commissionStatementText(statement: CommissionStatement, t: DocumentTranslator): string {
  switch (statement.kind) {
    case 'RATE':
      return t('portal.commission.figure-rate', { rate: statement.grossRate });
    case 'AMOUNT':
      return t('portal.commission.figure-amount', { amount: statement.grossAmount });
    case 'BUYER_FREE':
      return t('portal.commission.buyer-free');
    case 'TENANT_FREE':
      return t('portal.commission.tenant-free');
  }
}

function addressLines(address: DocumentAddress): string[] {
  const street = [address.street, address.houseNumber].filter(isPresent).join(' ');
  const city = [address.postalCode, address.city].filter(isPresent).join(' ');

  return [street, city].filter((line) => line !== '');
}

const isPresent = (part: string | null): part is string => part !== null && part !== '';

function assertNever(value: never): never {
  throw new Error(`Unhandled document value kind: ${JSON.stringify(value)}`);
}
